import { ChevronLeftIcon, EnterIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Badge, Box, Button, Callout, Card, Flex, Spinner, Tabs, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CheckboxGroupComponent from "../../components/inputs/CheckboxGroup/CheckboxGroupComponent";
import { DragAndDropFileUpload } from "../../components/inputs/FileUpload/DragAndDropFileUpload";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetCurrentAcademicYearQuery } from "../../hooks/useGetCurrentAcademicYearQuery";
import { useGetPaymentTypesQuery } from "../../hooks/useGetPaymentTypesQuery";
import { useGetSchoolBankConfigQuery } from "../../hooks/useGetSchoolBankConfigQuery";
import { resetStudentData } from "../../redux/slices/school/student";
import { resetSubscriptionData } from "../../redux/slices/school/subscription";
import { resetUserData } from "../../redux/slices/users/user";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { getPlanSubjects } from "../../services/school/PlanSubjects";
import { updateSubscription } from "../../services/school/student/updateSubscription"; // API de atualização
import { getSubjectsReprovedQuery } from "../../services/school/subject/getSubjects";
import { stringToBoolean } from "../../utils/utilities";
import { UserPreview } from "../Users/UserPreview";

export const ViewSubscription = () => {
  const [activeSegment, setActiveSegment] = useState('user');
  const { data: schoolBankConfigsData, status: schoolBankConfigsStatus, isError: isErrorSchoolschoolBankConfigs, isLoading: isLoadingSchoolschoolBankConfigs } = useGetSchoolBankConfigQuery({ PageNumber: 1, PageSize: 400 });
  const { data: paymentTypesData, status: paymentTypesStatus, isError: isErrorPaymentTypes, isLoading: isLoadingPaymentTypes } = useGetPaymentTypesQuery({ PageNumber: 1, PageSize: 400 });

  const studentData = useSelector((state: RootState) => state.student.student);
  const subscriptionData = useSelector((state: RootState) => state.subscription);
  const paymentTotal = subscriptionData?.paymentsReferences?.at(0)?.amount ?? "";
  const { data: currentYear } = useGetCurrentAcademicYearQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mutationUpdateSubscription = useMutation({ mutationFn: updateSubscription, mutationKey: "updateSubscription" });
  const loadingState = mutationUpdateSubscription.isLoading;

  const { data: reprovedSubjectsData, isLoading: isLoadingReprovedSubjects, refetch: fetchReprovedSubjects, isError: isErrorReprovedSubjects, status: reprovedSubjectsStatus } =
    useQuery(
      ['getSubjectsReprovedQuery', studentData?.schoolCode],
      async () => {
        try {
          const data = await getSubjectsReprovedQuery({ PageNumber: 1, PageSize: 400, StudentCode: `${studentData?.schoolCode}`, Year: currentYear?.year, Semester: subscriptionData?.semester });
          return data;
        }
        catch {
        }
      },
      {
        enabled: !!studentData?.schoolCode && !!subscriptionData?.semester,
        refetchOnWindowFocus: false,
        retry: false,
      }
    );
  const { data: planSubjectsData, isLoading: isLoadingPlanSubjectsData, isError: isErrorPlanSubjectsData, refetch: refreshPlanSubjects, status: planSubjectsStatus } =
    useQuery(
      ['getPlanSubjectsQuery'],
      async () => {
        try {
          const data = await getPlanSubjects({
            PageSize: 400,
            PageNumber: 1,
            Level: undefined,
            Semester: subscriptionData?.semester,
            IdPlan: subscriptionData?.idPlan,
            IdCourse: studentData?.idCourse
          }
          );
          const responseData = { ...data }
          responseData.items = responseData.items.filter(i => i.level <= subscriptionData?.level!);
          return responseData;
        } catch { }

      },
      {
        enabled: !!subscriptionData?.idPlan && !!subscriptionData?.level && !!subscriptionData?.semester,
        refetchOnWindowFocus: false,
        retry: false
      }
    );
  type FormValues = {
    retrySubjectsReproved: string[];
    subjectsSubscriptions: string[];
    payment?: {
      isPaymentByEntityReference?: boolean
      entity?: string
      totalPrice?: string | number
      reference?: string,
      idStudentDebt?: string,
      paymentProofSubscription?: any
      paymentProofPerSubject?: any
      paymentProof?: any
      idBankEntity?: string,
      idPaymentTypeSubscription?: string,
      idPaymentTypePerSubject?: string,
    }
  };
  const { control, handleSubmit, setValue, formState: { errors }, watch, getValues, resetField } = useForm<FormValues>({
    defaultValues: {
      retrySubjectsReproved: [],
      subjectsSubscriptions: [],
      // payment: {
      //   idPaymentTypeSubscription: subscriptionData?.paymentsReferences?.at(0)?.idPaymentType ?? "",
      //   isPaymentByEntityReference: subscriptionData?.payment?.isPaymentByEntityReference ?? false,
      //   idBankEntity: subscriptionData?.payment?.entity ?? "",
      //   paymentProof: subscriptionData?.payment?.paymentProof ?? "",
      // }
    },
    mode: 'onBlur',
  });
  useEffect(() => {
    if (reprovedSubjectsData?.items && subscriptionData?.planSubjects) {
      const selectedReproved = reprovedSubjectsData.items
        .filter(rs => subscriptionData?.planSubjects?.some(cs => cs.idSubject === rs.idSubject))
        .map(item => JSON.stringify(item));
      setValue('retrySubjectsReproved', selectedReproved);
    }

    if (planSubjectsData?.items && subscriptionData?.planSubjects) {
      const selectedRegular = planSubjectsData.items
        .filter(cs => subscriptionData?.planSubjects?.some(sc => sc.idSubject === cs.idSubject))
        .map(item => JSON.stringify({
          id: item.id,
          subject: item.subject,
          semester: item.semester,
          idSubject: item.idSubject,
          idSubjectPrecedence: item.idSubjectPrecedence,
          level: item.level,
        }));

      setValue('subjectsSubscriptions', selectedRegular);
    }
  }, [reprovedSubjectsData, planSubjectsData, subscriptionData, setValue]);


  const onSubmit = handleSubmit(async (data) => {
    try {

      const result = await mutationUpdateSubscription.mutateAsync({ ...subscriptionData, idValidated: true });
      if (result) {
        toast.success("Subscrição validada com sucesso");
        navigate(routes.subscriptionsValidation);
      }
    } catch (err: any) {
      toast.error(`${mutationUpdateSubscription.error ?? err?.message}`);
    }
  });

  const handleBack = async () => {
    dispatch(resetUserData());
    dispatch(resetStudentData());
    dispatch(resetSubscriptionData());
    navigate(routes.subscriptionsValidation);
  }

  const watchPaymentType = watch('payment.isPaymentByEntityReference');

  // const handleOnChangePaymentType = (e: any) => {
  //   const paymentType = stringToBoolean(getValues('payment.isPaymentByEntityReference'))
  //   if (paymentType) {
  //     resetField('payment.paymentProof')
  //   }
  //   if (!paymentType) {
  //     resetField('payment.idBankEntity')
  //   }
  // };

  return (
    <Flex direction="column" gap="2">

      {/* <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
        <Callout.Root>
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            Valor a pagar :  {paymentTotal}
          </Callout.Text>
        </Callout.Root>
      </Flex> */}

      <form onSubmit={onSubmit} noValidate>

        <Card size="5">
          <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate) => {
            setActiveSegment(tabToNavigate);
            if (tabToNavigate.includes('subscription')) {
              fetchReprovedSubjects();
              refreshPlanSubjects();
            }

            if (tabToNavigate.includes('payments')) {

              const bankEntityDB = subscriptionData?.payment?.entity;

              if (bankEntityDB) {

                const schoolBankConfig = schoolBankConfigsData?.items?.find(
                  item => item.bankEntity == bankEntityDB
                );

                if (schoolBankConfig?.id) {
                  setValue("payment.idBankEntity", schoolBankConfig.id);
                } else {
                  setValue("payment.idBankEntity", "");
                }
              }
            }
          }}>
            <Tabs.List>
              <Tabs.Trigger value="user">Dados gerais</Tabs.Trigger>
              <Tabs.Trigger value="subscription">Disciplinas</Tabs.Trigger>
              <Tabs.Trigger value="payments">Pagamentos</Tabs.Trigger>
            </Tabs.List>
            <Box pt="3">
              <Tabs.Content value="user">
                <UserPreview />
              </Tabs.Content>
              <Tabs.Content value="subscription">
                <Flex direction={'column'} gap='4'>
                  <CheckboxGroupComponent
                    name="retrySubjectsReproved"
                    label="Disciplinas em atraso"
                    control={control}
                    errors={errors}
                    options={reprovedSubjectsData?.items && reprovedSubjectsData?.items?.sort((a, b) => a.level - b.level)?.map(reprovedSubjects => {
                      return ({ value: JSON.stringify(reprovedSubjects), label: `${reprovedSubjects.subject} - ${reprovedSubjects.semester}º Semestre` });
                    })}
                    style={{ width: '100%' }}
                    itemsError={isErrorReprovedSubjects}
                    itemsLoading={isLoadingReprovedSubjects}
                  />
                  <CheckboxGroupComponent
                    name="subjectsSubscriptions"
                    label="Disciplinas do semestre"
                    control={control}
                    errors={errors}
                    options={planSubjectsData?.items?.sort((a, b) => a.level - b.level)?.map(item => {
                      const isAbleToDoEqual = reprovedSubjectsData?.items.find(rs => rs.idSubject === item.idSubjectPrecedence);
                      return {
                        value: JSON.stringify({
                          id: item.id,
                          subject: item.subject,
                          semester: item.semester,
                          idSubject: item.idSubject,
                          idSubjectPrecedence: item.idSubjectPrecedence,
                          level: item.level,
                        }),
                        label: `${item.subject} (${item.level}ºAno ${item.semester}ºSemestre)`.concat((item?.isCore ? (isAbleToDoEqual && isAbleToDoEqual?.id && " (Nuclear)  Não será inscrito") : (isAbleToDoEqual && isAbleToDoEqual?.id && " (Opcional)  Não será inscrito")) ?? ""),
                        isCoreBadge: item.isCore ? (
                          <>
                            <Badge color="green">Nuclear</Badge>
                            {isAbleToDoEqual && isAbleToDoEqual?.id && (
                              <Badge color="red">Não será inscrito</Badge>
                            )}
                          </>
                        ) : (
                          <>
                            <Badge color="gray">Opcional</Badge>
                            {isAbleToDoEqual && isAbleToDoEqual?.id && (
                              <Badge color="red">Não será inscrito</Badge>
                            )}
                          </>
                        ),
                      };
                    })}

                    style={{ width: '100%' }}
                    itemsError={isErrorPlanSubjectsData}
                    itemsLoading={isLoadingPlanSubjectsData}
                  />
                </Flex>

              </Tabs.Content>

              <Tabs.Content value='payments'>

                <Flex gap='6' direction='column'>
                  
                    {subscriptionData?.paymentsReferences?.map(({entity,referenceCode, amount }, index) => (
                  <Callout.Root>
                    <Callout.Icon>
                      <InfoCircledIcon />
                    </Callout.Icon>
                    <Callout.Text>
                      Entidade: {entity}, Referência: {referenceCode}, Valor: {amount}
                    </Callout.Text>
                  </Callout.Root>
                    ))}

{/* 
                  <RadioGroupComponent name="payment.isPaymentByEntityReference" label="Tipo de emolumento" control={control} errors={errors}
                    style={{ width: '50%' }}
                    options={[
                      { value: "true", label: "Entidade Referencia" }
                      , { value: "false", label: "Comprovativo" }]}
                    fieldProps={{
                      style: { display: 'flex', flexDirection: 'row', gap: '16px' },
                      onChange: handleOnChangePaymentType
                    }}
                  />
                  <SelectComponent
                    name="payment.idBankEntity"
                    label="Entidade Bancária"
                    control={control}
                    errors={errors}
                    itemsLoading={isLoadingSchoolschoolBankConfigs}
                    itemsError={isErrorSchoolschoolBankConfigs}
                    options={schoolBankConfigsData?.items?.map(item => ({
                      value: item.id!,
                      label: String(item.bankEntity!) ?? "",
                    })) ?? []}
                    style={{ width: '100%' }}
                    fieldProps={{ disabled: !stringToBoolean(watchPaymentType) }}
                  />
                  <Box>
                    <Text as="label" size="2">Comprovativo de Pagamento</Text>
                    <Controller
                      name={`payment.paymentProof`}
                      control={control}

                      disabled={stringToBoolean(watchPaymentType)}
                      render={({ field }) => (
                        <DragAndDropFileUpload
                          onFileDrop={(file) => {
                            field.onChange(file);
                          }}
                          label={`Arraste ou selecione o arquivo`}
                          accept="*"
                          selectedFile={field.value}
                          isReadOnly={stringToBoolean(watchPaymentType)}
                          disabled={stringToBoolean(watchPaymentType)}
                        />
                      )}
                    />
                    {errors.payment?.paymentProof && (
                      <Text color="red" size="1">{`${errors.payment.paymentProof.message}`}</Text>
                    )}
                  </Box>
                  <SelectComponent
                    name="payment.idPaymentTypeSubscription"
                    label="Valor da inscrição"
                    control={control}
                    errors={errors}
                    itemsLoading={isLoadingPaymentTypes}
                    itemsError={isErrorPaymentTypes}
                    options={paymentTypesData?.items?.filter(i => Number((i?.internalCode!)) === 26).map(item => ({
                      value: item.id!,
                      label: String(item.description!) ?? "",
                    })) ?? []}
                    style={{ width: '100%' }}
                  />
                  <SelectComponent
                    name="payment.idPaymentTypePerSubject"
                    label="Valor por disciplina"
                    control={control}
                    errors={errors}
                    itemsLoading={isLoadingPaymentTypes}
                    itemsError={isErrorPaymentTypes}
                    options={paymentTypesData?.items?.filter(i => Number((i?.internalCode!)) === 27).map(item => ({
                      value: item.id!,
                      label: String(item.description!) ?? "",
                    })) ?? []}
                    style={{ width: '100%' }}
                  />

                  <Callout.Root>
                    <Callout.Icon>
                      <InfoCircledIcon />
                    </Callout.Icon>
                    <Callout.Text>
                      Valor a pagar : {paymentTotal}
                    </Callout.Text>
                  </Callout.Root> */}
                </Flex>
              </Tabs.Content>
            </Box>
          </Tabs.Root>
        </Card>
        <Flex mt="4" justify="end" gap={'4'}>
          <Button color="red" onClick={handleBack} disabled={loadingState}>
            <ChevronLeftIcon /> Voltar
          </Button>
          <Button type="submit" variant="surface" disabled={loadingState}>
            {loadingState ? <Spinner loading /> : <EnterIcon />} Validar
          </Button>
        </Flex>
      </form>
    </Flex>
  );
};